import { LevelScale, Scale, TimeScale } from 'app/common/graph/Scale';
import React from 'react';
import './Axis.less';
import TimeUtils from 'app/common/util/TimeUtils';
import FlightLevelUtils from 'app/common/util/FlightLevelUtils';
import GraphUtils from 'app/common/graph/GraphUtils';
import Utils from '../util/Utils';

export enum AxisOrientation {
    TOP,
    BOTTOM,
    LEFT,
    RIGHT,
}

interface CommonAxisProps {
    orientation: AxisOrientation;
    tickInterval: number;
    width: number;
    height: number;
    x: number;
    y: number;
    title?: string;
    offsetForHorizontalLabelText?: number;
    offsetForVerticalLabelText?: number;
}
export interface AxisProps extends CommonAxisProps {
    scale: Scale;
    format?(value: number): string;
    classNameFor?(value: number): string;
}

/**
 * In order to see labels as the beginning and end of an axis, we add on a small offset
 */
const OFFSET_FOR_HORIZONTAL_LABEL_TEXT = 15;
const OFFSET_FOR_VERTICAL_LABEL_TEXT = 10;

const TICK_LENGTH = 5;

/**
 * A Pure REACT (no D3) Axis.
 * This Axis works with a scale object that defines how source (i.e. graphical) co-ordinates map onto
 * target coordinates.
 * This Axis is often wrapped by other components to give business-oriented target coordinates (Time, Flight Level, etc).
 *
 * Implementation Details
 * In order to ensure clipping, this component is rendered as an SVG, with a viewBox coordinate system that follows
 * the scale.source.
 *
 */
export class Axis extends React.PureComponent<AxisProps> {
    renderTitle() {
        if (this.props.title) {
            const midPoint = Math.abs(this.props.scale.source.size) / 2;
            switch (this.props.orientation) {
                case AxisOrientation.LEFT:
                    // want writingMode : sideways-lr, but not supported on Chrome yet.
                    return (
                        <text
                            writingMode="vertical-lr"
                            textAnchor="middle"
                            dominantBaseline="text-after-edge"
                            x={0}
                            y={midPoint}>
                            {this.props.title}
                        </text>
                    );
                case AxisOrientation.RIGHT:
                    // not getting same behaviour on Chrome vs Firefox when using dominantBaseline hanging etc
                    return (
                        <text
                            writingMode="vertical-lr"
                            textAnchor="middle"
                            dominantBaseline="auto"
                            x={this.props.width - 5}
                            y={midPoint}>
                            {this.props.title}
                        </text>
                    );
                case AxisOrientation.TOP:
                    return (
                        <text textAnchor="middle" dominantBaseline="hanging" x={midPoint} y={0}>
                            {this.props.title}
                        </text>
                    );
                case AxisOrientation.BOTTOM:
                    return (
                        <text textAnchor="middle" x={midPoint} y={this.props.height}>
                            {this.props.title}
                        </text>
                    );
            }
        }
    }

    renderAxisLine() {
        switch (this.props.orientation) {
            case AxisOrientation.LEFT:
                return (
                    <line
                        x1={this.props.width - 1}
                        y1={this.props.scale.source.from}
                        x2={this.props.width - 1}
                        y2={this.props.scale.source.to}
                    />
                );
            case AxisOrientation.RIGHT:
                return <line x1={0} y1={this.props.scale.source.from} x2={0} y2={this.props.scale.source.to} />;
            case AxisOrientation.TOP:
                return (
                    <line
                        x1={this.props.scale.source.from}
                        y1={this.props.height - 1}
                        x2={this.props.scale.source.to}
                        y2={this.props.height - 1}
                    />
                );
            case AxisOrientation.BOTTOM:
                return <line x1={this.props.scale.source.from} y1={0} x2={this.props.scale.source.to} y2={0} />;
        }
    }

    getTickPath(sourceValue: number) {
        switch (this.props.orientation) {
            case AxisOrientation.LEFT:
                return ' M' + this.props.width + ',' + sourceValue + ' l' + -TICK_LENGTH + ',0';
            case AxisOrientation.RIGHT:
                return ' M0,' + sourceValue + ' l' + TICK_LENGTH + ',0';
            case AxisOrientation.TOP:
                return ' M' + sourceValue + ',' + this.props.height + ' l0,' + -TICK_LENGTH;
            case AxisOrientation.BOTTOM:
                return ' M' + sourceValue + ',0 l0,' + TICK_LENGTH;
        }
    }

    /**
     * Render ticks, taking care to only render those inside the scale.source range
     */
    renderTicks() {
        const range = this.props.scale.targetLimits || this.props.scale.target;
        let value = range.getFirstValue(this.props.tickInterval);
        const increment = range.from <= range.to ? this.props.tickInterval : -this.props.tickInterval;
        let path = '';
        while (range.contains(value)) {
            const sourceVal = this.props.scale.toSource(value);
            if (this.props.scale.source.contains(sourceVal)) {
                path += this.getTickPath(sourceVal);
            }
            value += increment;
        }
        return <path d={path} />;
    }

    renderTickText(sourceValue: number, text: string, className: string) {
        const offset = TICK_LENGTH + 2;
        switch (this.props.orientation) {
            case AxisOrientation.LEFT:
                return (
                    <text
                        x={this.props.width - offset}
                        y={sourceValue}
                        key={sourceValue}
                        textAnchor="end"
                        dominantBaseline="middle"
                        className={className}>
                        {text}
                    </text>
                );
            case AxisOrientation.RIGHT:
                return (
                    <text
                        x={offset}
                        y={sourceValue}
                        key={sourceValue}
                        textAnchor="start"
                        dominantBaseline="middle"
                        className={className}>
                        {text}
                    </text>
                );
            case AxisOrientation.TOP:
                return (
                    <text
                        x={sourceValue}
                        y={this.props.height - offset}
                        key={sourceValue}
                        textAnchor="middle"
                        dominantBaseline="text-top"
                        className={className}>
                        {text}
                    </text>
                );
            case AxisOrientation.BOTTOM:
                return (
                    <text
                        x={sourceValue}
                        y={offset}
                        key={sourceValue}
                        textAnchor="middle"
                        dominantBaseline="hanging"
                        className={className}>
                        {text}
                    </text>
                );
        }
    }

    getLabelFor(value: number) {
        return this.props.format ? this.props.format(value) : value + '';
    }

    getClassNameFor(value: number) {
        return this.props.classNameFor ? this.props.classNameFor(value) : undefined;
    }

    /**
     * Render the axis text.
     * Note : we do not attempt to clip ourselves (unlike renderTicks) .. this ensures
     * that the first/last labels are visible, even if on the edge of the axis.
     */
    renderText() {
        const range = this.props.scale.targetLimits || this.props.scale.target;
        let value = range.getFirstValue(this.props.tickInterval);
        const increment = range.from <= range.to ? this.props.tickInterval : -this.props.tickInterval;
        const nodes = [];
        while (range.contains(value)) {
            nodes.push(
                this.renderTickText(
                    this.props.scale.toSource(value),
                    this.getLabelFor(value),
                    this.getClassNameFor(value),
                ),
            );
            value += increment;
        }
        return nodes;
    }

    getOffsetForHorizontalLabelText = () => {
        return Utils.isExists(this.props.offsetForHorizontalLabelText)
            ? this.props.offsetForHorizontalLabelText
            : OFFSET_FOR_HORIZONTAL_LABEL_TEXT;
    };

    getOffsetForVerticalLabelText = () => {
        return Utils.isExists(this.props.offsetForVerticalLabelText)
            ? this.props.offsetForVerticalLabelText
            : OFFSET_FOR_VERTICAL_LABEL_TEXT;
    };

    /**
     * Define a viewbox (min-x min-y width height) so that the SVG is working with 'scale.source' coordinates
     * This is necessary since <Svg> coordinates would otherwise start at 0,0.
     * We also need to add on small margins, so that the text for 1st and last labels are hopefully visible.
     */
    getViewBox() {
        const source = this.props.scale.getIncreasingSource();
        switch (this.props.orientation) {
            case AxisOrientation.BOTTOM:
            case AxisOrientation.TOP:
                return (
                    source.from -
                    this.getOffsetForHorizontalLabelText() +
                    ' 0 ' +
                    (source.size + this.getOffsetForHorizontalLabelText() * 2) +
                    ' ' +
                    this.props.height
                );
            case AxisOrientation.LEFT:
            case AxisOrientation.RIGHT:
                return (
                    '0 ' +
                    (source.from - this.getOffsetForVerticalLabelText()) +
                    ' ' +
                    this.props.width +
                    ' ' +
                    (source.size + this.getOffsetForVerticalLabelText() * 2)
                );
        }
    }

    /**
     * Render the Axis as an SVG, so that automatic clipping handles off-screen labels/ticks when zooming/panning.
     * Note : we adjust slightly the desired size, adding on small offsets,
     * so that the first/last labels (which are centered around the tick mark), may be visible.
     */
    renderAsSvg() {
        let width, height, x, y;
        switch (this.props.orientation) {
            case AxisOrientation.LEFT:
            case AxisOrientation.RIGHT:
                x = this.props.x;
                y = this.props.y - this.getOffsetForVerticalLabelText();
                width = this.props.width;
                height = this.props.height + this.getOffsetForVerticalLabelText() * 2;
                break;
            case AxisOrientation.TOP:
            case AxisOrientation.BOTTOM:
                x = this.props.x - this.getOffsetForHorizontalLabelText();
                y = this.props.y;
                width = this.props.width + this.getOffsetForHorizontalLabelText() * 2;
                height = this.props.height;
                break;
        }
        return (
            //we are forced to render as an SVG (as opposed to simple <g>, since we need clipping to be performed.
            <svg className="axis" viewBox={this.getViewBox()} width={width} height={height} x={x} y={y}>
                {this.renderAxisLine()}
                {this.renderTicks()}
                {this.renderText()}
                {this.renderTitle()}
            </svg>
        );
    }

    renderAsGroup() {
        // an alternative implementation would return a <g>
        // However, this would require the user to manually setup clip rectangle definitions on the parent SVG.
    }

    render() {
        return this.renderAsSvg();
    }
}

export enum TimeLabelFormat {
    HH_OR_MM,
    HH_MM,
}
export interface TimeAxisProps extends CommonAxisProps {
    timeScale: TimeScale;
    labelFormat: TimeLabelFormat;
}

const DEFAULT_TIME_TICK_INTERVALS_IN_SECS = [60, 5 * 60, 10 * 60, 20 * 60, 30 * 60, 60 * 60, 120 * 60];

export class TimeAxis extends React.PureComponent<TimeAxisProps> {
    format = (value: number) => {
        const date = this.props.timeScale.toDate(value);
        const hhmm = TimeUtils.formatHHMM(date);
        switch (this.props.labelFormat) {
            case TimeLabelFormat.HH_MM:
                return hhmm;
            case TimeLabelFormat.HH_OR_MM:
                return date.getTime() % TimeUtils.HR_TO_MS === 0 ? hhmm.substring(0, 2) : hhmm.substring(3, 5);
        }
    };

    classNameFor = (value: number) => {
        switch (this.props.labelFormat) {
            case TimeLabelFormat.HH_MM:
                return undefined;
            case TimeLabelFormat.HH_OR_MM:
                const date = this.props.timeScale.toDate(value);
                return date.getTime() % TimeUtils.HR_TO_MS === 0 ? 'timeAxisHH' : 'timeAxisMM';
        }
    };

    static chooseTickInterval(
        timeScale: TimeScale,
        numTicks: number,
        labelFormat: TimeLabelFormat,
        orientation: AxisOrientation,
        possibleIntervalsInSec: number[] = DEFAULT_TIME_TICK_INTERVALS_IN_SECS,
    ) {
        const exampleLabel = labelFormat === TimeLabelFormat.HH_OR_MM ? '09' : '09:55';
        return GraphUtils.chooseBestTickInterval(
            timeScale.getScale(),
            numTicks,
            possibleIntervalsInSec,
            GraphUtils.getApproximateLabelSize(orientation, exampleLabel),
        );
    }

    render() {
        const { timeScale, ...otherProps } = this.props;

        return (
            <Axis {...otherProps} scale={timeScale.getScale()} format={this.format} classNameFor={this.classNameFor} />
        );
    }
}

export interface LevelAxisProps extends CommonAxisProps {
    levelScale: LevelScale;
}

const DEFAULT_LEVEL_TICK_INTERVALS_IN_FEET = [500, 1000, 2000, 5000, 10000];
export class LevelAxis extends React.PureComponent<LevelAxisProps> {
    format = (value: number) => {
        return FlightLevelUtils.getLevelValue(this.props.levelScale.toFlightLevel(value)).toString();
    };

    static chooseTickInterval(
        levelScale: LevelScale,
        numTicks: number,
        orientation: AxisOrientation,
        posibleIntervalsInFeet: number[] = DEFAULT_LEVEL_TICK_INTERVALS_IN_FEET,
    ) {
        return GraphUtils.chooseBestTickInterval(
            levelScale.getScale(),
            numTicks,
            posibleIntervalsInFeet,
            GraphUtils.getApproximateLabelSize(orientation, '350'),
        );
    }

    render() {
        const { levelScale, ...otherProps } = this.props;
        return <Axis {...otherProps} scale={levelScale.getScale()} format={this.format} />;
    }
}

export interface CountAxisProps extends CommonAxisProps {
    countScale: Scale;
}

const DEFAULT_COUNT_TICK_INTERVALS = [20, 10, 5, 2, 1];
export class CountAxis extends React.PureComponent<CountAxisProps> {
    static chooseTickInterval(
        countScale: Scale,
        numTicks: number,
        orientation: AxisOrientation,
        posibleIntervals: number[] = DEFAULT_COUNT_TICK_INTERVALS,
    ) {
        // Reduce distance between labels to 5px (instead of 10px by default)
        return GraphUtils.chooseBestTickInterval(
            countScale,
            numTicks,
            posibleIntervals,
            GraphUtils.getApproximateLabelSize(orientation, '10') - 5,
        );
    }

    render() {
        const { countScale, ...otherProps } = this.props;
        return <Axis {...otherProps} scale={countScale} />;
    }
}
